import {
  DEFAULT_CONFIG,
  CLASS_CODE_CHARS,
} from './constants';
import { LabEnvironment } from './types';

// LabEnvironment is used by callers; keep the import to avoid re-exporting concerns
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type _LabEnvironmentRef = LabEnvironment;

/**
 * Generate a random class code
 */
export function generateClassCode(length: number = DEFAULT_CONFIG.CLASS_CODE_LENGTH): string {
  let result = '';
  const chars = CLASS_CODE_CHARS;

  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return result;
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Normalize email to lowercase
 */
export function normalizeEmail(email: string): string {
  return email.toLowerCase().trim();
}

/**
 * Generate random student ID (non-sequential for security)
 * Returns 6-character alphanumeric string (e.g., "7k3m9p")
 * @deprecated Use generateInstanceId instead for new enrollments
 */
export function generateRandomStudentId(length: number = 6): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';

  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return result;
}

/**
 * Generate unique instance ID (6-char alphanumeric)
 * Used for both enrollment instanceId and lab instance identification
 * Format: {6 lowercase alphanumeric} (e.g., "a7k9m2")
 * Avoids confusing characters: 0, O, 1, I, l
 */
export function generateInstanceId(length: number = 6): string {
  const chars = 'abcdefghjkmnpqrstuvwxyz23456789'; // Matches INSTANCE_ID_CONFIG.CHARS
  let result = '';

  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return result;
}

/**
 * DEPRECATED: Use generateRandomStudentId instead
 * Generate student ID for a class (sequential - not secure)
 */
export function generateStudentId(index: number, prefix: string = 'student'): string {
  return `${prefix}${index}`;
}

/**
 * @deprecated Lab groups are no longer used in one-instance-per-student architecture
 * Calculate lab group assignment (a, b, c, etc.)
 */
export function calculateLabGroup(
  studentIndex: number,
  maxStudentsPerGroup: number = 20
): string {
  const groupIndex = Math.floor(studentIndex / maxStudentsPerGroup);
  return String.fromCharCode(97 + groupIndex); // 97 = 'a'
}

/**
 * @deprecated Lab groups are no longer used in one-instance-per-student architecture
 * Parse lab group and student number from student ID and group
 */
export function parseStudentAssignment(studentId: string, labGroup: string): {
  studentNumber: number;
  groupLetter: string;
} {
  const studentNumber = parseInt(studentId.replace(/\D/g, ''), 10) || 0;
  return {
    studentNumber,
    groupLetter: labGroup
  };
}

/**
 * Format date for display
 */
export function formatDate(dateString: string, includeTime: boolean = true): string {
  const date = new Date(dateString);
  const dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  };

  if (includeTime) {
    dateOptions.hour = '2-digit';
    dateOptions.minute = '2-digit';
    dateOptions.timeZoneName = 'short';
  }

  return date.toLocaleDateString('en-US', dateOptions);
}

/**
 * Format duration between two dates
 */
export function formatDuration(startDate: string, endDate: string): string {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffMs = end.getTime() - start.getTime();

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  if (days > 0) {
    return `${days} day${days > 1 ? 's' : ''}, ${hours} hour${hours > 1 ? 's' : ''}`;
  } else {
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  }
}

/**
 * Check if a date/time is in the past
 */
export function isPastDate(dateString: string): boolean {
  return new Date(dateString) < new Date();
}

/**
 * Check if current time is within a date range
 */
export function isDateInRange(startDate: string, endDate: string): boolean {
  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);
  return now >= start && now <= end;
}

/**
 * Sanitize string for use in URLs or identifiers
 */
export function sanitizeIdentifier(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Validate short class ID format
 * Must be 3-15 chars, alphanumeric + hyphens, lowercase
 */
export function isValidShortClassId(shortClassId: string): boolean {
  const regex = /^[a-z0-9-]{3,15}$/;
  return regex.test(shortClassId);
}

/**
 * Generate a unique identifier
 */
export function generateId(prefix?: string): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 9);
  return prefix ? `${prefix}-${timestamp}-${random}` : `${timestamp}-${random}`;
}

/**
 * Delay execution (useful for testing and rate limiting)
 */
export function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Retry function with exponential backoff
 */
export async function retry<T>(
  fn: () => Promise<T>,
  maxAttempts: number = 3,
  baseDelayMs: number = 1000
): Promise<T> {
  let lastError: Error;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;

      if (attempt === maxAttempts) {
        throw lastError;
      }

      // Exponential backoff with jitter
      const delayMs = baseDelayMs * Math.pow(2, attempt - 1) + Math.random() * 1000;
      await delay(delayMs);
    }
  }

  throw lastError!;
}
